/**
 * Nana's Coffee — Lead Qualification Funnel
 * ------------------------------------------------------------------
 * A four-step qualification flow that turns cold email traffic into
 * scored wholesale leads with a personalized coffee program.
 *
 * Everything a merchant needs to tune — employee ranges, coffee
 * programs, scoring weights, shipping threshold, webhook URL — is
 * read from data-* attributes rendered by the Liquid section from
 * the theme editor. This file contains no business copy or numbers
 * that should live in the editor; see /docs/RULES.md.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  const DRINK_TYPES = {
    espresso_drinks: { label: 'Espresso & lattes', color: 'var(--nc-copper)' },
    drip_coffee: { label: 'Drip coffee', color: 'var(--nc-sage)' },
    cold_brew_iced: { label: 'Cold brew & iced', color: 'var(--nc-warn)' },
  };

  const EQUIPMENT_LABELS = {
    espresso: 'Espresso machine',
    batch_brew: 'Batch brewer',
    cold_brew_machine: 'Cold brew machine',
    grinder: 'Dedicated grinder',
  };

  // Which piece of equipment a drink preference implies, so the
  // equipment recommendation stays consistent with what they said they
  // serve even if they didn't separately pick that machine.
  const FORMAT_TO_EQUIPMENT = {
    espresso_drinks: 'espresso',
    drip_coffee: 'batch_brew',
    cold_brew_iced: 'cold_brew_machine',
  };

  // Which coffee format a piece of desired equipment implies, so the
  // product recommendation stays consistent with the machine they want
  // even if they didn't separately pick that drink type.
  const EQUIPMENT_TO_FORMAT = {
    espresso: 'espresso_drinks',
    batch_brew: 'drip_coffee',
    cold_brew_machine: 'cold_brew_iced',
  };

  class NanaLeadFunnel {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-nc-form]');
      this.steps = Array.from(root.querySelectorAll('[data-nc-step]'));
      this.railSteps = Array.from(root.querySelectorAll('[data-nc-rail-step]'));
      this.railConnectors = Array.from(root.querySelectorAll('[data-nc-rail-connector]'));
      this.statusEl = root.querySelector('[data-nc-status]');
      this.current = 0;

      this.rules = this.parseRules();
      this.state = {
        business_name: '', contact_name: '', email: '', phone: '', phone_ext: '', address: '',
        business_type: '', region: '',
        coffee_drinkers_range: null, coffee_drinkers_custom: '', drink_preference: [],
        program_status: '', equipment_ownership: '', program_comment: '', equipment: [],
        contact_comment: '',
      };
      this.splitPct = {};
      this._splitKey = null;

      this.bindNav();
      this.bindRail();
      this.bindChoiceTiles();
      this.bindConditionals();
      this.bindSplitBar();
      this.bindSubmit();
      this.bindContactToggle();
      this.bindNumericMasks();
      this.showStep(0);
    }

    /** Rules arrive as JSON in a data attribute set by the section schema/blocks. */
    parseRules() {
      const raw = this.root.getAttribute('data-nc-rules');
      try {
        return JSON.parse(raw);
      } catch (e) {
        console.error('Nana Lead Funnel: could not parse rules JSON', e);
        return { coffeeDrinkersRanges: [], programs: [], scoring: {}, shipping: {} };
      }
    }

    // ---------------------------------------------------------------
    // Navigation
    // ---------------------------------------------------------------
    bindNav() {
      this.root.querySelectorAll('[data-nc-next]').forEach((btn) =>
        btn.addEventListener('click', () => this.goNext())
      );
      this.root.querySelectorAll('[data-nc-back]').forEach((btn) =>
        btn.addEventListener('click', () => this.goBack())
      );
    }

    // Rail nodes only jump to a step already reached — never skip ahead
    // past unvalidated steps.
    bindRail() {
      this.railSteps.forEach((el, i) => {
        el.addEventListener('click', () => {
          if (i <= this.current) this.showStep(i);
        });
      });
    }

    goNext() {
      if (!this.validateStep(this.current)) return;
      this.captureStep(this.current);

      if (this.current === this.steps.length - 2) {
        // Last input step complete — compute results before revealing them.
        this.computeAndRenderResults();
      }
      if (this.current < this.steps.length - 1) {
        this.showStep(this.current + 1);
      }
    }

    goBack() {
      if (this.current > 0) this.showStep(this.current - 1);
    }

    showStep(index) {
      this.current = index;
      this.steps.forEach((el, i) => el.classList.toggle('is-active', i === index));
      this.railSteps.forEach((el, i) => {
        el.classList.toggle('is-active', i === index);
        el.classList.toggle('is-done', i < index);
      });
      this.railConnectors.forEach((el, i) => el.classList.toggle('is-filled', i < index));
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // ---------------------------------------------------------------
    // Choice tiles (business type, ranges, equipment)
    // ---------------------------------------------------------------
    bindChoiceTiles() {
      this.root.querySelectorAll('.nc-choice input').forEach((input) => {
        input.addEventListener('change', () => {
          const group = input.closest('[data-nc-choice-group]');
          if (!group) return;
          const multi = group.dataset.multi === 'true';
          if (!multi) {
            group.querySelectorAll('.nc-choice').forEach((c) => c.classList.remove('is-selected'));
          }
          input.closest('.nc-choice').classList.toggle('is-selected', input.checked);
          if (group.querySelectorAll('input:checked').length > 0) group.classList.remove('has-error');
        });
      });
    }

    // ---------------------------------------------------------------
    // Conditional fields — e.g. equipment ownership only matters if the
    // visitor is under an existing supplier contract.
    // ---------------------------------------------------------------
    bindConditionals() {
      const conditionals = Array.from(this.root.querySelectorAll('[data-nc-conditional]'));
      if (!conditionals.length) return;
      const evaluate = () => {
        conditionals.forEach((el) => {
          const [field, expected] = el.dataset.ncConditional.split(':');
          const checked = this.root.querySelector(`[name="${field}"]:checked`);
          el.classList.toggle('is-visible', !!checked && checked.value === expected);
        });
      };
      this.root.querySelectorAll('[data-nc-toggle]').forEach((input) => {
        input.addEventListener('change', evaluate);
      });
      evaluate();
    }

    // ---------------------------------------------------------------
    // Split bar — how the daily volume divides between espresso, drip,
    // and cold brew. A single type is implicitly 100%; 2-3 types get a
    // draggable bar so the visitor can set the real mix themselves.
    // ---------------------------------------------------------------
    bindSplitBar() {
      this.root.querySelectorAll('input[data-field="drink_preference"]').forEach((input) => {
        input.addEventListener('change', () => this.updateSplitBar());
      });
      this.updateSplitBar();
    }

    getSelectedDrinks() {
      return Array.from(this.root.querySelectorAll('input[data-field="drink_preference"]:checked')).map((i) => i.value);
    }

    updateSplitBar() {
      const wrap = this.root.querySelector('[data-nc-split]');
      if (!wrap) return;
      const selected = this.getSelectedDrinks();

      if (selected.length < 2) {
        wrap.classList.remove('is-visible');
        this._splitKey = null;
        this.splitPct = {};
        selected.forEach((k) => { this.splitPct[k] = 100; });
        return;
      }

      wrap.classList.add('is-visible');

      const key = selected.join(',');
      if (this._splitKey !== key) {
        this._splitKey = key;
        const even = Math.floor(100 / selected.length);
        this.splitPct = {};
        selected.forEach((k, i) => {
          this.splitPct[k] = i === selected.length - 1 ? 100 - even * (selected.length - 1) : even;
        });
      }

      this.renderSplitBar(selected);
    }

    renderSplitBar(selected) {
      const bar = this.root.querySelector('[data-nc-split-bar]');
      const legend = this.root.querySelector('[data-nc-split-legend]');
      if (!bar || !legend) return;
      bar.innerHTML = '';
      legend.innerHTML = '';

      let cumulative = 0;
      const boundaries = [];
      selected.forEach((key, i) => {
        const meta = DRINK_TYPES[key] || { label: key, color: 'var(--nc-ink-30)' };
        const seg = document.createElement('div');
        seg.className = 'nc-split-bar__segment';
        seg.style.width = this.splitPct[key] + '%';
        seg.style.background = meta.color;
        bar.appendChild(seg);

        cumulative += this.splitPct[key];
        if (i < selected.length - 1) boundaries.push(cumulative);

        const item = document.createElement('span');
        item.className = 'nc-split-legend__item';
        item.innerHTML = `<i style="background:${meta.color}"></i>${meta.label} <b data-nc-split-value="${key}">${Math.round(this.splitPct[key])}%</b>`;
        legend.appendChild(item);
      });

      boundaries.forEach((pos, i) => {
        const handle = document.createElement('button');
        handle.type = 'button';
        handle.className = 'nc-split-bar__handle';
        handle.style.left = pos + '%';
        handle.setAttribute('aria-label', 'Drag to adjust the mix');
        bar.appendChild(handle);
        this.bindSplitHandle(handle, selected, i);
      });
    }

    /** Updates existing segment/handle/legend elements in place — used
     *  during drag so we never recreate the handle mid-gesture (that
     *  would drop its pointer capture and freeze the drag). */
    updateSplitVisuals(selected) {
      const bar = this.root.querySelector('[data-nc-split-bar]');
      const legend = this.root.querySelector('[data-nc-split-legend]');
      if (!bar || !legend) return;
      const segments = bar.querySelectorAll('.nc-split-bar__segment');
      const handles = bar.querySelectorAll('.nc-split-bar__handle');

      let cumulative = 0;
      const boundaries = [];
      selected.forEach((key, i) => {
        if (segments[i]) segments[i].style.width = this.splitPct[key] + '%';
        cumulative += this.splitPct[key];
        if (i < selected.length - 1) boundaries.push(cumulative);
        const valueEl = legend.querySelector(`[data-nc-split-value="${key}"]`);
        if (valueEl) valueEl.textContent = Math.round(this.splitPct[key]) + '%';
      });
      boundaries.forEach((pos, i) => { if (handles[i]) handles[i].style.left = pos + '%'; });
    }

    bindSplitHandle(handle, selected, boundaryIndex) {
      const bar = this.root.querySelector('[data-nc-split-bar]');
      const minGap = 5;

      const boundariesFromPct = () => {
        let cum = 0;
        const arr = [];
        selected.forEach((k, i) => {
          cum += this.splitPct[k];
          if (i < selected.length - 1) arr.push(cum);
        });
        return arr;
      };

      const applyPosition = (pos) => {
        const boundaries = boundariesFromPct();
        const lower = boundaryIndex === 0 ? 0 : boundaries[boundaryIndex - 1];
        const upper = boundaryIndex === boundaries.length - 1 ? 100 : boundaries[boundaryIndex + 1];
        pos = clamp(pos, lower + minGap, upper - minGap);
        boundaries[boundaryIndex] = pos;

        let prev = 0;
        selected.forEach((k, i) => {
          const next = i === selected.length - 1 ? 100 : boundaries[i];
          this.splitPct[k] = Math.round(next - prev);
          prev = next;
        });
        this.updateSplitVisuals(selected);
      };

      const onMove = (clientX) => {
        const rect = bar.getBoundingClientRect();
        const pos = ((clientX - rect.left) / rect.width) * 100;
        applyPosition(pos);
      };

      handle.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        handle.setPointerCapture(e.pointerId);
        const move = (ev) => onMove(ev.clientX);
        const up = () => {
          handle.removeEventListener('pointermove', move);
          handle.removeEventListener('pointerup', up);
        };
        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', up);
      });

      handle.addEventListener('keydown', (e) => {
        const boundaries = boundariesFromPct();
        if (e.key === 'ArrowLeft') { applyPosition(boundaries[boundaryIndex] - 1); e.preventDefault(); }
        if (e.key === 'ArrowRight') { applyPosition(boundaries[boundaryIndex] + 1); e.preventDefault(); }
      });
    }

    // ---------------------------------------------------------------
    // Validation + capture
    // ---------------------------------------------------------------
    validateStep(index) {
      const step = this.steps[index];
      let valid = true;
      let firstInvalid = null;
      step.querySelectorAll('[data-required]').forEach((field) => {
        const conditionalAncestor = field.closest('[data-nc-conditional]');
        if (conditionalAncestor && !conditionalAncestor.classList.contains('is-visible')) return;
        const wrapper = field.closest('.nc-field') || field.closest('[data-nc-choice-group]');
        const isGroup = field.hasAttribute('data-nc-choice-group');
        let filled;
        if (isGroup) {
          filled = field.querySelectorAll('input:checked').length > 0;
        } else if (field.matches('input[type=email]')) {
          filled = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim());
        } else {
          filled = field.value && field.value.trim() !== '';
        }
        if (wrapper) wrapper.classList.toggle('has-error', !filled);
        if (!filled) {
          valid = false;
          if (!firstInvalid) firstInvalid = wrapper || field;
        }
      });
      // Missing answers can be scrolled out of view on a long step — jump
      // to the first one so it never looks like the button "did nothing".
      if (!valid && firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return valid;
    }

    captureStep(index) {
      const step = this.steps[index];
      step.querySelectorAll('[data-field]').forEach((field) => {
        const key = field.dataset.field;
        if (field.type === 'checkbox') {
          if (!Array.isArray(this.state[key])) this.state[key] = [];
          const val = field.value;
          if (field.checked && !this.state[key].includes(val)) this.state[key].push(val);
          if (!field.checked) this.state[key] = this.state[key].filter((v) => v !== val);
        } else if (field.type === 'radio') {
          if (field.checked) this.state[key] = field.value;
        } else {
          this.state[key] = field.value;
        }
      });
    }

    // ---------------------------------------------------------------
    // Recommendation + scoring engine
    // ---------------------------------------------------------------
    computeAndRenderResults() {
      const s = this.state;
      const rules = this.rules;

      // --- Estimated monthly consumption (lb) -----------------------
      // daily cups (exact if "Other", otherwise the TOP of the selected
      // range — we plan for peak demand, not the average). Each format
      // (espresso/drip/cold brew) has its own gram-per-serving rate,
      // since a double espresso and a drip cup don't use the same
      // amount of ground coffee. If no drink mix was given, fall back
      // to one generic rate for the whole volume. Delivery frequency is
      // set by client demand, not derived here.
      let drinkersEstimate;
      if (s.coffee_drinkers_range === 'other') {
        drinkersEstimate = Number(s.coffee_drinkers_custom) || 0;
      } else {
        const drinkers = rules.coffeeDrinkersRanges.find((r) => r.id === s.coffee_drinkers_range)
          || rules.coffeeDrinkersRanges[0]
          || { min: 25, max: 50 };
        drinkersEstimate = drinkers.max || drinkers.min;
      }
      const workDays = rules.consumption.workingDaysPerMonth || 22;
      const fallbackLbPerCup = rules.consumption.lbPerCup || 0.025;

      const selectedDrinks = s.drink_preference || [];
      // Cold brew is sold as ready-made insert packs, not ground coffee
      // by the pound — it never enters the lb/price-tier math below.
      const lbFormats = selectedDrinks.filter((key) => key !== 'cold_brew_iced');
      const formatLbs = {};
      let estimatedLbs;
      if (selectedDrinks.length) {
        lbFormats.forEach((key) => {
          const pct = this.splitPct[key] ?? Math.round(100 / selectedDrinks.length);
          const rate = rules.formatLbPerCup?.[key] ?? fallbackLbPerCup;
          const cupsForFormat = drinkersEstimate * (pct / 100);
          formatLbs[key] = Math.round(cupsForFormat * workDays * rate);
        });
        estimatedLbs = Object.values(formatLbs).reduce((a, b) => a + b, 0);
      } else {
        estimatedLbs = Math.round(drinkersEstimate * workDays * fallbackLbPerCup);
      }

      // --- Cold brew inserts — its own product and pricing entirely --
      // Each insert makes a fixed volume (default 1.5L); divide that by
      // an assumed average cup size (default 8oz) to get cups/insert,
      // then figure out how many inserts (and 6-packs) the visitor's
      // monthly cold brew volume needs.
      let coldBrewInsert = null;
      if (selectedDrinks.includes('cold_brew_iced')) {
        const pct = this.splitPct['cold_brew_iced'] ?? Math.round(100 / selectedDrinks.length);
        const dailyCups = drinkersEstimate * (pct / 100);
        const monthlyCups = dailyCups * workDays;
        const cb = rules.coldBrewInsert || {};
        const mlPerCup = (cb.cupOz || 8) * 29.5735;
        const cupsPerInsert = (cb.insertMl || 1500) / mlPerCup;
        // insertsNeeded is only an intermediate figure used to round up
        // to whole packs — never shown on its own, since it isn't what
        // actually gets shipped (e.g. 9 needed still ships as 2 packs =
        // 12 inserts, and 12 is the number that belongs on the quote).
        const insertsNeeded = Math.max(1, Math.ceil(monthlyCups / cupsPerInsert));
        const packSize = cb.packSize || 6;
        const packsNeeded = Math.ceil(insertsNeeded / packSize);
        const totalInserts = packsNeeded * packSize;
        const packPrice = cb.packPrice || 30;
        coldBrewInsert = {
          monthlyCups: Math.round(monthlyCups),
          packsNeeded,
          totalInserts,
          packPrice,
          totalCost: Math.round(packsNeeded * packPrice * 100) / 100,
          productName: cb.productName || 'Cold Brew Insert Pack (6-count)',
        };
      }

      // --- Program match ---------------------------------------------
      // Programs are sorted by minLbs descending; first one the estimate
      // clears wins. Falls back to the smallest program.
      const sortedPrograms = [...rules.programs].sort((a, b) => b.minLbs - a.minLbs);
      const program = sortedPrograms.find((p) => estimatedLbs >= p.minLbs)
        || rules.programs[rules.programs.length - 1]
        || { name: 'Custom program', tagline: '', products: [], recommendedEquipment: [] };

      // --- Recommended products ----------------------------------------
      // Espresso/drip are the same medium roast, sold by the lb — only
      // the grind differs. Cold brew is a completely different product
      // (ready-made insert packs), priced and quantified separately.
      // Falls back to the matched program's generic products if nothing
      // was selected.
      let products;
      if (selectedDrinks.length) {
        products = selectedDrinks.map((key) => {
          if (key === 'cold_brew_iced' && coldBrewInsert) {
            const packWord = coldBrewInsert.packsNeeded === 1 ? 'pack' : 'packs';
            return {
              name: coldBrewInsert.productName,
              tag: `${coldBrewInsert.packsNeeded} ${packWord} (${coldBrewInsert.totalInserts} inserts) — $${coldBrewInsert.totalCost.toFixed(2)}/mo`,
              variantId: rules.coffeeFormats?.cold_brew_iced?.variantId || null,
              qty: coldBrewInsert.packsNeeded, // 1 cart unit = 1 pack of 6
            };
          }
          const format = rules.coffeeFormats?.[key];
          const bagSize = format?.bagSizeLbs || 5;
          const qty = Math.max(1, Math.ceil((formatLbs[key] || 0) / bagSize));
          return { name: format?.name || key, tag: `${format?.tag ? format.tag + ' — ' : ''}${formatLbs[key]} lb/mo`, variantId: format?.variantId || null, qty };
        });
      } else {
        products = [...(program.products || [])];
      }

      // Equipment implies a format too, even if it wasn't picked as a
      // drink preference — wanting an espresso machine means they'll
      // need the espresso grind, wanting a cold brew machine means
      // they'll need the cold brew pack.
      const wantedEquipmentIds = s.equipment || [];
      wantedEquipmentIds.forEach((eqId) => {
        const formatKey = EQUIPMENT_TO_FORMAT[eqId];
        if (!formatKey || selectedDrinks.includes(formatKey)) return;
        const format = rules.coffeeFormats?.[formatKey];
        if (!format?.name || products.some((p) => p.name === format.name)) return;
        products.push({ name: format.name, tag: `${format.tag ? format.tag + ' — ' : ''}based on your equipment`, variantId: format.variantId || null, qty: 1 });
      });

      // --- Recommended equipment ----------------------------------------
      // Directly tied to what the visitor said they'd like to have — if
      // they picked "Espresso machine", we recommend an espresso
      // machine. Falls back to the matched program's generic list only
      // when nothing was picked. A drink preference also implies its
      // machine — selecting "Cold brew & iced" but not the cold brew
      // machine still gets the cold brew machine recommended, since
      // they'll need it to serve what they said they drink.
      const wantedEquipment = s.equipment || [];
      const recommendedEquipment = wantedEquipment.length
        ? wantedEquipment.map((id) => ({ id, name: EQUIPMENT_LABELS[id] || id }))
        : [...(program.recommendedEquipment || [])];

      selectedDrinks.forEach((drinkKey) => {
        const eqId = FORMAT_TO_EQUIPMENT[drinkKey];
        if (!eqId || wantedEquipment.includes(eqId) || recommendedEquipment.some((e) => e.id === eqId)) return;
        recommendedEquipment.push({ id: eqId, name: EQUIPMENT_LABELS[eqId] || eqId });
      });

      // --- Lead score (0-100), weighted by editable rule config ------
      const w = rules.scoring;
      let score = 0;
      score += clamp01(drinkersEstimate / (w.coffeeDrinkersCap || 30)) * (w.coffeeWeight || 50);
      score += (w.programStatusScores?.[s.program_status] ?? 0.5) * (w.programWeight || 15);
      score += clamp01((s.equipment || []).length / (w.equipmentCap || 3)) * (w.equipmentWeight || 15);
      score = Math.round(clamp(score, 0, 100));

      const grade = score >= (w.hotThreshold || 75) ? 'Hot'
        : score >= (w.warmThreshold || 50) ? 'Warm' : 'Developing';

      // --- Free shipping eligibility -----------------------------------
      const shippingEligible = estimatedLbs >= (rules.shipping.freeShippingMinLbs || 20);

      // --- Wholesale price per lb ----------------------------------------
      // Independent from the program tier above — program is about
      // equipment/support level, price is purely volume-based. Tiers are
      // sorted ascending; the highest one the estimate clears wins.
      const pricingTiers = rules.pricingTiers || [];
      const sortedTiers = [...pricingTiers].sort((a, b) => b.minLbs - a.minLbs);
      const priceTier = sortedTiers.find((t) => estimatedLbs >= t.minLbs) || pricingTiers[0];
      const pricePerLb = priceTier?.pricePerLb || 0;
      const coffeeCost = Math.round(estimatedLbs * pricePerLb * 100) / 100;
      const monthlyCost = Math.round((coffeeCost + (coldBrewInsert?.totalCost || 0)) * 100) / 100;

      this.results = {
        program, estimatedLbs, products, recommendedEquipment, score, grade, shippingEligible,
        pricingTiers, priceTier, pricePerLb, coffeeCost, coldBrewInsert, monthlyCost,
      };

      this.renderResults(this.results);
    }

    renderResults(r) {
      const root = this.root;
      const set = (sel, text) => { const el = root.querySelector(sel); if (el) el.textContent = text; };
      const copy = this.rules.copy || {};

      // Fresh results (from editing answers and coming back) should
      // always be resubmittable — clear any earlier disabled state and
      // leftover status message from a previous submission.
      root.querySelectorAll('[data-nc-submit]').forEach((btn) => btn.removeAttribute('disabled'));
      if (this.statusEl) {
        this.statusEl.classList.remove('is-visible', 'is-error');
        this.statusEl.innerHTML = '';
      }

      const businessType = this.state.business_type || 'business';
      const fillTemplate = (tpl) => (tpl || '').replace(/\[business_type\]/g, businessType);
      set('[data-nc-out="result_heading"]', fillTemplate(copy.resultHeading || 'Your [business_type] coffee program'));
      set('[data-nc-out="result_subheading"]', fillTemplate(copy.resultSubheading || "Here's how much of our wholesale program you're unlocking."));

      const gradeLabels = {
        Hot: copy.gradeHotLabel || 'Premium benefits unlocked',
        Warm: copy.gradeWarmLabel || 'Great benefits unlocked',
        Developing: copy.gradeDevelopingLabel || 'Just getting started',
      };

      set('[data-nc-out="program_name"]', r.program.name);
      set('[data-nc-out="program_tagline"]', r.program.tagline || '');
      set('[data-nc-out="grade"]', gradeLabels[r.grade] || r.grade);

      const lbsEl = root.querySelector('[data-nc-out="estimated_lbs"]');
      if (lbsEl) animateNumber(lbsEl, 0, r.estimatedLbs, 700, ' lb / month');

      const scoreEl = root.querySelector('[data-nc-out="score"]');
      if (scoreEl) animateNumber(scoreEl, 0, r.score, 900);

      const gauge = root.querySelector('[data-nc-gauge]');
      if (gauge) {
        gauge.style.setProperty('--pct', r.score);
        gauge.classList.toggle('nc-gauge--hot', r.grade === 'Hot');
      }

      const shipNote = root.querySelector('[data-nc-shipping-note]');
      if (shipNote) {
        shipNote.classList.toggle('is-ineligible', !r.shippingEligible);
        shipNote.textContent = r.shippingEligible
          ? (this.rules.copy?.shippingEligible || 'This volume qualifies for free wholesale shipping.')
          : (this.rules.copy?.shippingIneligible || 'Add volume to qualify for free wholesale shipping.');
      }

      const productList = root.querySelector('[data-nc-product-list]');
      if (productList) {
        productList.innerHTML = (r.products || [])
          .map((p) => `<li><span>${escapeHtml(p.name)}</span><span class="nc-tag">${escapeHtml(p.tag || '')}</span></li>`)
          .join('');
      }

      const equipmentList = root.querySelector('[data-nc-equipment-list]');
      if (equipmentList) {
        equipmentList.innerHTML = r.recommendedEquipment.length
          ? r.recommendedEquipment.map((e) => `<li><span>${escapeHtml(e.name)}</span><span class="nc-tag">Recommended</span></li>`).join('')
          : '<li><span>No specific equipment needed for this program.</span></li>';
      }

      // Note: pricePerLb/monthlyCost are still computed in
      // computeAndRenderResults() and included in the lead payload for
      // internal use (quotes, CRM) — just not displayed to the visitor.

      // --- Buy directly ---------------------------------------------------
      // Adds the real linked products to cart, at the actual computed
      // quantity for each (bags for espresso/drip based on their bag
      // size in lb, packs for cold brew) when the merchant has connected
      // them; otherwise falls back to a general shop link. Equipment
      // interest never blocks the coffee purchase — it rides along as
      // cart attributes so a Shopify Flow can tag the order and trigger
      // a follow-up, without ever being added (or priced) as a line item.
      const buyNowEl = root.querySelector('[data-nc-buy-now]');
      if (buyNowEl) {
        const items = (r.products || []).filter((p) => p.variantId).map((p) => `${p.variantId}:${p.qty || 1}`);
        const base = items.length ? `/cart/${items.join(',')}` : (this.rules.buyNowFallbackUrl || '/collections/all');
        const wantedEquipment = this.state.equipment || [];
        if (items.length && wantedEquipment.length) {
          const equipmentNames = wantedEquipment.map((id) => EQUIPMENT_LABELS[id] || id).join(', ');
          const params = new URLSearchParams({
            'attributes[Wants Equipment]': 'Yes',
            'attributes[Equipment Interest]': equipmentNames,
          });
          buyNowEl.href = `${base}?${params.toString()}`;
        } else {
          buyNowEl.href = base;
        }
      }

      // Dynamic primary CTA: "Request an official quote" leads in most
      // cases. If they want equipment, "Contact us" takes over as
      // primary instead, since that needs a conversation. Buy Directly
      // stays secondary either way — it's still fully active, just not
      // the default emphasis.
      const wantsEquipment = (this.state.equipment || []).length > 0;
      const quoteBtn = root.querySelector('[data-nc-submit][data-nc-action="quote"]');
      const contactToggleBtn = root.querySelector('[data-nc-contact-toggle]');
      if (buyNowEl) {
        buyNowEl.classList.remove('nc-btn--primary');
        buyNowEl.classList.add('nc-btn--secondary');
      }
      if (quoteBtn && contactToggleBtn) {
        quoteBtn.classList.toggle('nc-btn--primary', !wantsEquipment);
        quoteBtn.classList.toggle('nc-btn--secondary', wantsEquipment);
        contactToggleBtn.classList.toggle('nc-btn--primary', wantsEquipment);
        contactToggleBtn.classList.toggle('nc-btn--secondary', !wantsEquipment);
      }

      // Contact box starts collapsed on every fresh render, with the
      // reply-to email pre-filled from step 1 (still editable here).
      const contactBox = root.querySelector('[data-nc-contact-box]');
      const contactEmailInput = root.querySelector('#nc-contact_email');
      if (contactBox) contactBox.classList.remove('is-visible');
      if (contactEmailInput) contactEmailInput.value = this.state.email || '';
    }

    // ---------------------------------------------------------------
    // "Contact us" reveals a message box instead of submitting right
    // away — the visitor confirms/edits their reply-to email there.
    // ---------------------------------------------------------------
    bindContactToggle() {
      const toggleBtn = this.root.querySelector('[data-nc-contact-toggle]');
      const box = this.root.querySelector('[data-nc-contact-box]');
      if (!toggleBtn || !box) return;
      toggleBtn.addEventListener('click', () => {
        const willShow = !box.classList.contains('is-visible');
        box.classList.toggle('is-visible', willShow);
        if (willShow) {
          const emailInput = box.querySelector('#nc-contact_email');
          if (emailInput && !emailInput.value) emailInput.value = this.state.email || '';
          box.querySelector('#nc-contact_comment')?.focus();
        }
      });
    }

    // ---------------------------------------------------------------
    // Phone fields — strip anything that isn't a digit (plus, for the
    // main phone number, common formatting characters) as the visitor
    // types or pastes, so letters can never end up in there.
    // ---------------------------------------------------------------
    bindNumericMasks() {
      this.root.querySelectorAll('[data-nc-phone-mask]').forEach((input) => {
        input.addEventListener('input', () => {
          const cleaned = input.value.replace(/[^0-9+\-() ]/g, '');
          if (cleaned !== input.value) input.value = cleaned;
        });
      });
      this.root.querySelectorAll('[data-nc-digits-only]').forEach((input) => {
        input.addEventListener('input', () => {
          const cleaned = input.value.replace(/[^0-9]/g, '');
          if (cleaned !== input.value) input.value = cleaned;
        });
      });
    }

    // ---------------------------------------------------------------
    // Submission — webhook (future CRM) + Shopify-native customer capture
    // ---------------------------------------------------------------
    bindSubmit() {
      this.root.querySelectorAll('[data-nc-submit]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (btn.disabled) return;
          this.submitLead(btn.dataset.ncAction || 'quote', btn);
        });
      });
    }

    async submitLead(action, btn) {
      this.captureStep(0);
      this.captureStep(this.current); // picks up the contact box's message/email, which live in the results step
      const r = this.results || {};
      const now = new Date();
      const expires = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const dateFmt = (d) => d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      const payload = {
        ...this.state,
        recommendation: r,
        requested_action: action, // 'quote' or 'call'
        source: 'nana-coffee-lead-funnel',
        submitted_at: now.toISOString(),
        page_url: window.location.href,

        // Flat fields for easy mapping into a Google Docs/PDF quote
        // template via Zapier or Make — avoids parsing nested JSON in
        // no-code tools.
        quote_number: `NC-${now.toISOString().slice(0, 10).replace(/-/g, '')}-${Math.floor(1000 + Math.random() * 9000)}`,
        quote_date: dateFmt(now),
        quote_expiration_date: dateFmt(expires),
        program_name: r.program?.name || '',
        program_tagline: r.program?.tagline || '',
        estimated_lbs: r.estimatedLbs || 0,
        price_per_lb: r.pricePerLb ? r.pricePerLb.toFixed(2) : '',
        coffee_cost: r.coffeeCost ? r.coffeeCost.toFixed(2) : '',
        cold_brew_packs_needed: r.coldBrewInsert?.packsNeeded || '',
        cold_brew_inserts_needed: r.coldBrewInsert?.totalInserts || '',
        cold_brew_cost: r.coldBrewInsert ? r.coldBrewInsert.totalCost.toFixed(2) : '',
        monthly_cost: r.monthlyCost ? r.monthlyCost.toFixed(2) : '',
        products_text: (r.products || []).map((p) => `${p.name} — ${p.tag}`).join('\n'),
        equipment_text: (r.recommendedEquipment || []).map((e) => e.name).join('\n'),
        wants_equipment: (this.state.equipment || []).length > 0 ? 'Yes' : 'No',
      };

      this.setStatus('Sending your program details…', false, true);

      const webhookUrl = this.root.getAttribute('data-nc-webhook-url');
      const tasks = [];

      if (webhookUrl) {
        tasks.push(
          fetch(webhookUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        );
      }

      // Shopify-native fallback: also fire the theme's customer/contact
      // form endpoint so a lead always lands somewhere even before a CRM
      // webhook is configured. See docs/INTEGRATIONS.md.
      const nativeForm = this.root.querySelector('[data-nc-native-form]');
      if (nativeForm) {
        nativeForm.querySelector('[name="contact[body]"]').value = JSON.stringify(payload, null, 2);
        tasks.push(fetch(nativeForm.action, { method: 'POST', body: new FormData(nativeForm) }));
      }

      try {
        await Promise.all(tasks);
        this.setStatus(this.rules.copy?.submitSuccess || 'Thank you — your coffee program is on its way to your inbox.', false, false);
        if (btn) btn.setAttribute('disabled', 'true');
      } catch (err) {
        console.error('Nana Lead Funnel: submission failed', err);
        this.setStatus(this.rules.copy?.submitError || 'Something went wrong sending your request. Please try again or email us directly.', true, false);
      }
    }

    setStatus(message, isError, isLoading) {
      if (!this.statusEl) return;
      this.statusEl.classList.add('is-visible');
      this.statusEl.classList.toggle('is-error', !!isError);
      this.statusEl.innerHTML = isLoading ? `<span class="nc-spinner"></span>${message}` : message;
    }
  }

  function clamp(n, min, max) { return Math.min(max, Math.max(min, n)); }
  function clamp01(n) { return clamp(n, 0, 1); }

  /** Eased count-up used for the score and consumption numbers on results. */
  function animateNumber(el, from, to, duration, suffix) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.textContent = to + (suffix || '');
      return;
    }
    const start = performance.now();
    const tick = (now) => {
      const t = clamp01((now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.round(from + (to - from) * eased);
      el.textContent = value + (suffix || '');
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-nc-funnel]').forEach((root) => new NanaLeadFunnel(root));
  });

  // Re-init when Shopify section editor re-renders this section.
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-nc-funnel]');
    if (root) new NanaLeadFunnel(root);
  });
})();
